// src/components/imagery/TimeStrip/ImageryStrip.jsx
//
// The imagery time strip, as rows of chips (footprint restyle S-e slice 3).
// Display only: it owns no data, fetches nothing and never touches a map. A
// page's adapter turns what it knows into rows (padplanner/imageryStripRows.js
// for SitePlanner; layers/imagery/exploreRows.js for Explore, slice 4a):
//
//   rows: [{ id, label, tone?: "primary"|"quiet", emptyText?, end?, message?,
//            chips: [{ key, label, title?, selected?, mark?, synthetic?,
//                      coverage?: "covered"|"none"|"unknown", zoomIn?: number|null,
//                      compare?: "A"|"B", playing? }] }]
//   onChip(rowId, key, chip)
//   announce   a second sentence for screen readers (Explore's status)
//
// What DB set (2026-11-02/03):
//   - each row is its own toolbar; Sentinel-2 is a quiet row, dashed;
//   - a row that overflows scrolls on its own, with no native scrollbar:
//     chevrons and an edge fade appear on a side that has more, only then;
//   - the selected chip, else the marked one, is scrolled into view, clear of
//     the fade, whenever it changes;
//   - one tab stop for the strip (a roving tabindex): Left/Right/Home/End
//     move along a row, Up/Down between rows (to that row's selected or
//     marked chip, else the same place); moving focus does not select;
//   - the selection is said in a polite live region;
//   - a synthetic_test capture's chip carries the SYNTHETIC badge (DI4);
//   - the chip's test id stays timestrip-epoch-<key>, the bar's timestrip-bar;
//   - Explore (slice 4a): a chip with no coverage at the map center is dimmed
//     (muted text, a dotted border); unknown coverage is drawn as covered, the
//     tooltip saying so; compare's pair carries A and B; the playing chip is
//     ringed; a row's message is one line under it, announced.
//   - S-e5 (both strips): a covered chip the map is zoomed out past (zoomIn,
//     the capture's minzoom) is not dimmed: full strength, a small zoom-in
//     mark, the tooltip saying from where. The click hands the page the chip,
//     which selects it and zooms the map there.
//
// The chevrons are for the pointer and are not tab stops: the arrow keys
// reach every chip, and a focused chip is scrolled into view.

import { Fragment, useCallback, useLayoutEffect, useRef, useState } from "react";
import { ZoomIn } from "lucide-react";

export const CHIP_SELECTED = "border-accent-action bg-accent-action/15 text-fg-primary";
export const CHIP_UNSELECTED = "border-fg-secondary/50 bg-bg-soft text-fg-secondary hover:border-fg-primary/70 hover:text-fg-primary";
export const SYNTHETIC_CHIP_TEXT = "SYNTHETIC";
// No coverage at the map center: muted text and a dotted border, so it never
// reads as merely disabled or loading (the tooltip says it in words).
export const CHIP_NO_COVERAGE = "border-dotted text-fg-secondary/60";
export const CHIP_PLAYING = "ring-2 ring-accent-action ring-offset-1 ring-offset-bg";

// The fade's width; a revealed chip is kept this far inside the row's edge.
const FADE_PX = 24;
const FADES = {
  none: undefined,
  left: `linear-gradient(to right, transparent 0, #000 ${FADE_PX}px, #000 100%)`,
  right: `linear-gradient(to right, #000 0, #000 calc(100% - ${FADE_PX}px), transparent 100%)`,
  both: `linear-gradient(to right, transparent 0, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), transparent 100%)`,
};

const chipId = (rowId, key) => `${rowId}\u0000${key}`;
const shownChip = (row) => row.chips?.find((c) => c.selected) ?? row.chips?.find((c) => c.mark) ?? null;

/** Bring a chip wholly into its row's view, clear of the fades. */
function reveal(scroller, chip) {
  if (!scroller || !chip) return;
  const left = chip.offsetLeft;
  const right = left + chip.offsetWidth;
  const view = scroller.clientWidth;
  const max = Math.max(0, scroller.scrollWidth - view);
  if (left - FADE_PX < scroller.scrollLeft) scroller.scrollLeft = Math.max(0, left - FADE_PX);
  else if (right + FADE_PX > scroller.scrollLeft + view) scroller.scrollLeft = Math.min(max, right + FADE_PX - view);
}

function Row({ row, tabStop, chipRefs, onChip, onKey, onFocusChip }) {
  const scrollerRef = useRef(null);
  const [more, setMore] = useState({ left: false, right: false });
  const chips = row.chips ?? [];
  const quiet = row.tone === "quiet";

  const measure = useCallback(() => {
    const s = scrollerRef.current;
    if (!s) return;
    const left = s.scrollLeft > 1;
    const right = s.scrollLeft + s.clientWidth < s.scrollWidth - 1;
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
  }, []);

  // The shown chip into view whenever it (or the chips) change, then measure;
  // and again whenever the row or a chip changes size, since the first reveal
  // can run on a layout that then settles (fonts loading, the slot growing).
  const shown = shownChip(row)?.key ?? null;
  const keys = chips.map((c) => c.key).join("\u0000");
  const settle = useCallback(() => {
    if (shown !== null) reveal(scrollerRef.current, chipRefs.current.get(chipId(row.id, shown)));
    measure();
  }, [shown, row.id, chipRefs, measure]);
  useLayoutEffect(settle, [keys, settle]);

  useLayoutEffect(() => {
    const s = scrollerRef.current;
    if (!s) return undefined;
    if (typeof ResizeObserver === "function") {
      const ro = new ResizeObserver(settle);
      ro.observe(s);
      for (const c of s.querySelectorAll("[data-chip-index]")) ro.observe(c);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", settle);
    return () => window.removeEventListener("resize", settle);
  }, [settle, keys]);

  const page = (dir) => {
    const s = scrollerRef.current;
    if (!s) return;
    const max = Math.max(0, s.scrollWidth - s.clientWidth);
    s.scrollLeft = Math.min(max, Math.max(0, s.scrollLeft + dir * Math.max(FADE_PX, s.clientWidth - 2 * FADE_PX)));
    measure();
  };

  const fade = more.left && more.right ? "both" : more.left ? "left" : more.right ? "right" : "none";
  const chevron = "absolute top-1/2 z-[1] flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-bg-border bg-bg/95 text-[11px] leading-none text-fg-primary shadow hover:border-fg-primary/70";

  return (
    <div className="flex min-w-0 items-center gap-2" data-testid={`timestrip-row-${row.id}`} data-tone={quiet ? "quiet" : "primary"}>
      <span className={quiet ? "shrink-0 whitespace-nowrap text-[11px] text-fg-secondary" : "op-label shrink-0 uppercase"}>{row.label}</span>
      <div className="relative min-w-0 flex-1">
        {more.left && (
          <button type="button" tabIndex={-1} className={`${chevron} left-0`} aria-label={`Earlier dates in ${row.label}`} onClick={() => page(-1)}>‹</button>
        )}
        <div ref={scrollerRef} data-strip-scroller="" data-fade={fade} onScroll={measure}
          role="toolbar" aria-label={row.label} aria-orientation="horizontal"
          className="no-scrollbar relative flex min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden py-0.5"
          style={{ maskImage: FADES[fade], WebkitMaskImage: FADES[fade] }}>
          {chips.length === 0 && <span className="whitespace-nowrap text-xs text-fg-secondary">{row.emptyText}</span>}
          {chips.map((c, i) => {
            const id = chipId(row.id, c.key);
            return (
              <button key={c.key} type="button" data-chip-index={i}
                ref={(el) => { if (el) chipRefs.current.set(id, el); else chipRefs.current.delete(id); }}
                data-testid={`timestrip-epoch-${c.key}`} data-row={row.id} data-synthetic={c.synthetic ? "true" : undefined}
                data-coverage={c.coverage ?? undefined} data-compare={c.compare ?? undefined} data-playing={c.playing ? "true" : undefined}
                data-zoom-in={Number.isInteger(c.zoomIn) ? String(c.zoomIn) : undefined}
                aria-pressed={Boolean(c.selected)} title={c.title ?? undefined}
                tabIndex={tabStop === id ? 0 : -1}
                onClick={() => onChip?.(row.id, c.key, c)}
                onFocus={() => { onFocusChip(row.id, c.key); reveal(scrollerRef.current, chipRefs.current.get(id)); }}
                onKeyDown={(e) => onKey(e, row.id, c.key)}
                className={[
                  "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[11px] leading-[16px]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-action",
                  c.selected ? CHIP_SELECTED : CHIP_UNSELECTED,
                  c.coverage === "none" ? CHIP_NO_COVERAGE : quiet ? "border-dashed" : "",
                  c.playing ? CHIP_PLAYING : "",
                ].join(" ")}>
                {c.synthetic && (
                  <span className="mr-1 rounded-sm bg-accent-red px-1 text-[10px] font-semibold tracking-wide text-white" data-testid="timestrip-synthetic">{SYNTHETIC_CHIP_TEXT}</span>
                )}
                {c.label}
                {Number.isInteger(c.zoomIn) && (
                  <ZoomIn aria-hidden="true" data-testid="timestrip-zoomin" className="ml-1 inline-block h-3 w-3 align-[-2px] text-fg-secondary" />
                )}
                {c.mark && <span className="ml-1 text-[10px] uppercase tracking-wide text-accent-action">{c.mark}</span>}
                {c.compare && (
                  <span className="ml-1 rounded-sm bg-accent-action px-1 text-[10px] font-semibold text-bg" data-testid="timestrip-compare">{c.compare}</span>
                )}
              </button>
            );
          })}
        </div>
        {more.right && (
          <button type="button" tabIndex={-1} className={`${chevron} right-0`} aria-label={`Later dates in ${row.label}`} onClick={() => page(1)}>›</button>
        )}
      </div>
      {row.end && <div className="flex shrink-0 items-center gap-2">{row.end}</div>}
    </div>
  );
}

/**
 * @param {object} p
 * @param {object[]|null} p.rows  null: nothing drawn
 * @param {(rowId: string, key: string, chip: object) => void} p.onChip
 * @param {string} [p.ariaLabel]
 */
export default function ImageryStrip({ rows, onChip, ariaLabel = "Imagery dates", announce = null }) {
  const chipRefs = useRef(new Map());
  const [focused, setFocused] = useState(null);
  const onFocusChip = useCallback((rowId, key) => setFocused(chipId(rowId, key)), []);
  if (!rows) return null;

  const live = rows.filter((r) => (r.chips ?? []).length > 0);
  const all = live.flatMap((r) => r.chips.map((c) => chipId(r.id, c.key)));
  const shownRow = live.find((r) => r.chips.some((c) => c.selected)) ?? live.find((r) => r.chips.some((c) => c.mark));
  const tabStop = focused && all.includes(focused) ? focused
    : shownRow ? chipId(shownRow.id, shownChip(shownRow).key)
    : all[0] ?? null;

  const onKey = (e, rowId, key) => {
    const r = live.findIndex((x) => x.id === rowId);
    const row = live[r];
    const i = row.chips.findIndex((c) => c.key === key);
    let target = null;
    if (e.key === "ArrowRight") target = [row, Math.min(i + 1, row.chips.length - 1)];
    else if (e.key === "ArrowLeft") target = [row, Math.max(i - 1, 0)];
    else if (e.key === "Home") target = [row, 0];
    else if (e.key === "End") target = [row, row.chips.length - 1];
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const other = live[e.key === "ArrowDown" ? Math.min(r + 1, live.length - 1) : Math.max(r - 1, 0)];
      const pick = other !== row ? shownChip(other) : null;
      target = [other, pick ? other.chips.indexOf(pick) : Math.min(i, other.chips.length - 1)];
    }
    if (!target) return;
    e.preventDefault();
    const [tr, ti] = target;
    const id = chipId(tr.id, tr.chips[ti].key);
    setFocused(id);
    chipRefs.current.get(id)?.focus();
  };

  const selected = rows.flatMap((r) => (r.chips ?? []).filter((c) => c.selected));
  const said = selected.map((c) => c.title || c.label).join("; ");

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-1 rounded-md border border-bg-border bg-bg/85 px-3 py-1.5 shadow"
      data-testid="timestrip-bar" role="group" aria-label={ariaLabel}>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <Row row={row} tabStop={tabStop} chipRefs={chipRefs} onChip={onChip} onKey={onKey} onFocusChip={onFocusChip} />
          {row.message && (
            <p className="m-0 text-[11px] leading-[14px] text-fg-primary" aria-live="polite" data-testid={`timestrip-message-${row.id}`}>{row.message}</p>
          )}
        </Fragment>
      ))}
      <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid="timestrip-status">{said ? `Selected: ${said}` : ""}</span>
      {announce != null && (
        <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid="timestrip-announce">{announce}</span>
      )}
    </div>
  );
}
